// src/pages/PrivacyPolicy.js
import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      {/* Page Header */}
      <header className="legal-header">
        <div className="legal-header-content">
          {/* Back Button */}
          <Link to="/" className="legal-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Home
          </Link>

          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-subtitle">
            Your privacy matters. Here's exactly how we collect, use, and
            protect your information.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="legal-container">
        {/* Introduction */}
        <section className="legal-section">
          <h2 className="legal-section-title">Introduction</h2>
          <p>
            When you use TheApp, you trust us with your information. That's why
            we insist upon the highest standard and are committed to keeping
            that trust. To maintain your trust in us, we start by helping you
            understand our privacy practices. This privacy policy ("policy")
            describes the information we collect, how we use and share your
            data, and your choices regarding this information.
          </p>
        </section>

        {/* Data Collection and Uses */}
        <section className="legal-section">
          <h2 className="legal-section-title">Data Collection and Uses</h2>

          <h3 className="legal-subsection-title">1. Summary</h3>
          <p>
            This policy describes how TheApp collects and uses personal
            information to provide our services. This policy applies to all
            users who register their information with TheApp, use the website,
            and any other services we provide. The practices described in this
            policy are subject to applicable laws in the places where we
            operate. This means that we only engage in the practices described
            in this policy.
          </p>

          <h3 className="legal-subsection-title">2. The Information We Collect</h3>
          <p>TheApp collects:</p>

          <h4 className="legal-sub-sub-title">Information you provide</h4>
          <ul className="legal-list">
            <li>
              <strong>User profile:</strong> We collect information when you
              create or update your TheApp account. This may include your login
              name, password, phone number, birth date, and your profile photo.
            </li>
            <li>
              <strong>Demographic data:</strong> We may collect demographic
              information about you, including through user surveys or we may
              also receive your demographic data from third parties.
            </li>
            <li>
              <strong>User Content:</strong> We may collect information that you
              submit when you contact our customer support, including your
              ratings, comments, and feedback.
            </li>
          </ul>

          <h4 className="legal-sub-sub-title">Contact List Information</h4>
          <ul className="legal-list">
            <li>
              <strong>What We Collect:</strong> With your explicit consent, we
              may access and upload information from your device's contact
              list. This information includes names, phone numbers, email
              addresses, and other contact details stored on your device.
            </li>
            <li>
              <strong>Why We Collect:</strong> We collect this information to
              enhance your user experience by enabling features such as
              connecting with friends, inviting contacts to join the app, etc.
            </li>
            <li>
              <strong>How We Use:</strong> The information from your contact
              list is used solely for the purposes described and is not shared
              with third parties without your explicit consent, except as
              required by law.
            </li>
          </ul>

          <h4 className="legal-sub-sub-title">
            Information created when you use our services
          </h4>
          <ul className="legal-list">
            <li>
              <strong>Location Information:</strong> TheApp collects your
              location data while you are using our services when the TheApp is
              running in the foreground. TheApp requires your location to find
              the nearest sports facilities, availability of venues, offers, and
              nearby matches happening.
            </li>
            <li>
              <strong>Transaction Information:</strong> We collect transaction
              details related to your use of our services, including the type of
              services you requested, your booking details, date and time the
              service was provided, amount charged, and payment method.
            </li>
            <li>
              <strong>Usage information:</strong> We collect information about
              how you interact with our services. This includes information such
              as access dates and times, app features or pages viewed, app
              crashes and other system activity, type of browser, and
              third-party sites or services you were using before interacting
              with our services. In some cases, we collect this information
              through cookies and similar technologies that create and maintain
              unique identifiers.
            </li>
            <li>
              <strong>Device Information:</strong> We may collect information
              about the devices you use to access services, including the
              hardware models, device IP address, operating systems and
              versions, software, preferred languages, unique device
              identifiers, serial numbers, device motion information, and mobile
              network information.
            </li>
          </ul>

          <h3 className="legal-subsection-title">
            3. How We Use Your Information
          </h3>
          <p>
            TheApp collects and uses the information to enable, personalize, and
            enhance your experience with us while using any of our services.
            TheApp does not sell or share your personal information with third
            parties for third-party direct marketing purposes.
          </p>
          <p>TheApp uses the information it collects for purposes including:</p>

          <h4 className="legal-sub-sub-title">Providing services and features</h4>
          <p>
            TheApp uses the information we collect to provide, personalize,
            maintain, and improve our products and services. This includes using
            the information to:
          </p>
          <ul className="legal-list">
            <li>Create and update your account.</li>
            <li>Verify your identity.</li>
            <li>
              Enable features to personalize your TheApp account, such as saving
              your favorite soccer fields, your frequently played time, and
              auto-input based on your previous bookings.
            </li>
            <li>
              Perform internal operations necessary to provide our services,
              including troubleshooting software bugs and operational problems,
              conducting data analysis, testing, research, and monitoring and
              analyzing usage and activity trends.
            </li>
          </ul>

          <h4 className="legal-sub-sub-title">Research and development</h4>
          <p>
            We may use the information we collect for testing, research,
            analysis, and product development. This allows us to improve and
            enhance the user experience of our services and helps us develop new
            features and products to meet your demands and needs.
          </p>

          <h4 className="legal-sub-sub-title">Communications from TheApp</h4>
          <p>
            TheApp may use the information we collect to communicate with you
            about products, services, promotions, news, surveys, updates, and
            events. This also helps us serve you relevant ads and content about
            our services and those of our business partners. You may receive
            some of these communications based on your profile as a TheApp user.
          </p>

          <h4 className="legal-sub-sub-title">
            Legal proceedings and requirements
          </h4>
          <p>
            We may use the information we collect to investigate or address
            claims or disputes relating to your use of TheApp services, or as
            otherwise allowed by applicable law, or as requested by regulators,
            government entities, and official inquiries.
          </p>
        </section>

        {/* Your Consent */}
        <section className="legal-section">
          <h2 className="legal-section-title">Your Consent</h2>
          <p>
            By using our services, including the app and website, or by
            providing your information, you consent to the collection of
            information described in the "Data Collection and Uses" section and
            how we use it as described in the "How We Use Your Information"
            section above. TheApp may share your information other than as
            described in this policy if we notify you and you consent to the
            sharing. You can request the deletion of your data by emailing{' '}
            <a href="mailto:support@theapp.in">support@theapp.in</a>.
          </p>
        </section>

        {/* Updates */}
        <section className="legal-section">
          <h2 className="legal-section-title">Updates to This Policy</h2>
          <p>
            We may occasionally update this policy. If you use our services
            after an update, you consent to the updated policy. If we make
            significant changes, we will notify you of the changes through
            TheApp or through other contact information you provided. By using
            our services after such notice, you consent to our updates to this
            policy.
          </p>
        </section>

        {/* Cancellation Policy */}
        <section className="legal-section">
          <h2 className="legal-section-title">Cancellation Policy</h2>

          <h3 className="legal-subsection-title">1. Cancellation by Users</h3>
          <p>Users may cancel a turf booking subject to the following conditions:</p>

          <h4 className="legal-sub-sub-title">1.1 Free Cancellation Window</h4>
          <ul className="legal-list">
            <li>
              <strong>Up to 24 hours before the booking start time:</strong>{' '}
              Users may cancel for free and receive a full refund to their
              original payment method or app wallet (as per user preference).
            </li>
            <li>
              <strong>Between 24 to 6 hours before the start time:</strong>{' '}
              Users will receive a 50% refund of the booking amount.
            </li>
            <li>
              <strong>Within 6 hours of the booking start time:</strong> No
              refund will be issued.
            </li>
          </ul>
          <p className="legal-note">
            <strong>Note:</strong> Refunds to original payment methods may take
            3–7 business days depending on your bank/payment provider.
          </p>

          <h3 className="legal-subsection-title">
            2. Cancellation by Turf Partners
          </h3>
          <p>
            In rare cases, a turf partner may cancel a booking due to
            operational reasons (e.g., maintenance, double booking, bad weather
            for outdoor turfs).
          </p>
          <ul className="legal-list">
            <li>
              In such cases, users will receive a 100% refund, either to their
              original payment method or as wallet credits.
            </li>
            <li>
              TheApp may offer additional credits or discounts as a gesture of
              goodwill depending on the situation.
            </li>
          </ul>

          <h3 className="legal-subsection-title">3. No-Show Policy</h3>
          <p>
            If a user does not show up for the booked slot and does not cancel
            in advance:
          </p>
          <ul className="legal-list">
            <li>The booking will be marked as a no-show.</li>
            <li>No refund will be provided.</li>
          </ul>

          <h3 className="legal-subsection-title">4. Rescheduling Policy</h3>
          <p>
            Users can request to reschedule a booking instead of cancelling it,
            subject to:
          </p>
          <ul className="legal-list">
            <li>Availability of the turf at the new requested time.</li>
            <li>
              A reschedule request being made at least 6 hours before the
              original booking time.
            </li>
            <li>Only one reschedule allowed per booking.</li>
          </ul>
          <p>
            If the request cannot be accommodated, the standard cancellation
            policy will apply.
          </p>

          <h3 className="legal-subsection-title">
            5. Refund Method & Timeline
          </h3>
          <ul className="legal-list">
            <li>
              Refunds to bank accounts or cards typically take 3–7 business
              days.
            </li>
            <li>Refunds to app wallet are instant upon approval.</li>
            <li>
              Refund status can be tracked in the "My Bookings" → "Cancellations"
              section of the app.
            </li>
          </ul>

          <h3 className="legal-subsection-title">6. Exceptions</h3>
          <ul className="legal-list">
            <li>
              Special cancellation terms may apply for bulk bookings, seasonal
              tournaments, or partner promotions. These will be clearly
              mentioned at the time of booking.
            </li>
            <li>
              For technical issues during booking or duplicate charges, users
              can contact support for a full resolution and refund.
            </li>
          </ul>

          <h3 className="legal-subsection-title">
            7. How to Cancel or Modify a Booking
          </h3>
          <p>To cancel or reschedule:</p>
          <ol className="legal-ordered-list">
            <li>Open TheApp.</li>
            <li>Go to "My Bookings".</li>
            <li>Select the booking you wish to modify.</li>
            <li>Choose "Cancel" or "Reschedule" and follow the instructions.</li>
          </ol>
          <p>
            For urgent cancellations, please contact customer support via the
            app or at <a href="mailto:support@queeslot.com">support@queeslot.com</a>.
          </p>

          <h3 className="legal-subsection-title">8. Contact Support</h3>
          <p>
            If you have any issues with your booking or refund, please contact:
          </p>
          <ul className="legal-contact-list">
            <li>
              <strong>Email:</strong>{' '}
              <a href="mailto:support@queeslot.com">support@queeslot.com</a>
            </li>
            
          </ul>
        </section>

        {/* Refund Policy */}
        <section className="legal-section">
          <h2 className="legal-section-title">Refund Policy</h2>
          <p className="legal-meta">
            <strong>Effective Date:</strong> [Insert Date]
          </p>
          <p className="legal-meta">
            <strong>Applies To:</strong> All bookings made via Queeslot
          </p>
          <p>
            At Queeslot, we strive to provide a smooth and user-friendly
            experience for all players and turf partners. This Refund Policy
            outlines the terms under which users are eligible for refunds and
            how such refunds will be processed.
          </p>

          <h3 className="legal-subsection-title">
            1. General Refund Eligibility
          </h3>
          <p>A user is eligible for a refund under the following conditions:</p>

          <h4 className="legal-sub-sub-title">1.1 Cancellation by User</h4>
          <p>
            Refunds for user-initiated cancellations will be governed by our
            Cancellation Policy. Depending on when the cancellation is made, the
            user may receive a full, partial, or no refund.
          </p>

          <h4 className="legal-sub-sub-title">1.2 Cancellation by Turf Partner</h4>
          <p>
            If the turf partner cancels a confirmed booking due to operational
            reasons (e.g., turf maintenance, weather disruptions, or double
            bookings), the user will receive a 100% refund of the amount paid.
          </p>

          <h4 className="legal-sub-sub-title">
            1.3 Technical Failures or Payment Errors
          </h4>
          <p>In cases where:</p>
          <ul className="legal-list">
            <li>A payment is debited but the booking fails,</li>
            <li>A double payment is made,</li>
            <li>Or system errors lead to booking issues,</li>
          </ul>
          <p>
            Users will be eligible for a full refund of the affected transaction.
          </p>

          <h3 className="legal-subsection-title">2. Refund Method</h3>
          <p>
            Refunds will be issued through one of the following methods based on
            the original payment mode and user preference:
          </p>
          <ul className="legal-list">
            <li>
              <strong>To Original Payment Method (UPI, Credit/Debit Card, Wallet):</strong>{' '}
              Refunds are processed within 3–7 business days from the approval
              date.
            </li>
            <li>
              <strong>To TheApp Wallet (Instant Credit):</strong> Refunds are
              credited within 1 hour of approval and can be used for future
              bookings.
            </li>
          </ul>
          <p>
            Users may choose their preferred refund method during the
            cancellation process or by contacting customer support.
          </p>

          <h3 className="legal-subsection-title">3. Non-Refundable Scenarios</h3>
          <p>Refunds will not be provided under the following circumstances:</p>
          <ul className="legal-list">
            <li>
              <strong>No-show:</strong> If the user does not show up for the
              booking and fails to cancel in advance.
            </li>
            <li>
              <strong>Late cancellation:</strong> Cancellations made outside the
              free cancellation or partial refund window as outlined in our
              Cancellation Policy.
            </li>
            <li>
              <strong>Violation of terms:</strong> If the user is found in
              breach of platform or venue rules (e.g., misconduct, misuse of
              turf, etc.).
            </li>
          </ul>

          <h3 className="legal-subsection-title">4. Partial Refunds</h3>
          <p>In case of:</p>
          <ul className="legal-list">
            <li>Shortened usage due to user choice or early exit,</li>
            <li>Downgrade in service (e.g., shifting to a smaller turf),</li>
          </ul>
          <p>
            Refunds will be reviewed on a case-by-case basis. Final discretion
            lies with TheApp.
          </p>

          <h3 className="legal-subsection-title">
            5. Dispute Resolution & Refund Appeals
          </h3>
          <p>
            If a user disagrees with the outcome of a cancellation or refund
            decision, they may:
          </p>
          <ul className="legal-list">
            <li>
              Email the full details to{' '}
              <a href="mailto:support@queeslot.com">support@queeslot.com</a>{' '}
              within 72 hours of the issue.
            </li>
            <li>
              Provide relevant screenshots, transaction IDs, or booking
              references.
            </li>
            <li>
              Our support team will review the request and respond within 3
              working days.
            </li>
          </ul>

          <h3 className="legal-subsection-title">6. Force Majeure</h3>
          <p>
            In case of cancellations due to events beyond control (e.g., natural
            disasters, government-imposed restrictions, pandemics, etc.),
            refund eligibility will be evaluated based on the situation. Where
            applicable, users may receive full or partial refunds, or reschedule
            options.
          </p>

          <h3 className="legal-subsection-title">
            7. Promotions, Discounts & Vouchers
          </h3>
          <p>
            Refunds on bookings made using promo codes, discounts, or gift
            vouchers:
          </p>
          <ul className="legal-list">
            <li>
              Only the actual amount paid (excluding discounts) will be
              refunded.
            </li>
            <li>
              Promo codes or coupons used during the original booking will not
              be reissued unless specifically stated.
            </li>
          </ul>

          <h3 className="legal-subsection-title">8. Refund Tracking</h3>
          <p>Users can track the status of their refunds by:</p>
          <ul className="legal-list">
            <li>
              Visiting <strong>My Bookings → Cancellations & Refunds</strong> in
              the app.
            </li>
            <li>Contacting our support team via chat or email.</li>
          </ul>

          <h3 className="legal-subsection-title">9. Contact & Support</h3>
          <p>For refund queries or disputes, contact:</p>
          <ul className="legal-contact-list">
            <li>
              <strong>Email:</strong>{' '}
              <a href="mailto:support@queeslot.com">support@queeslot.com</a>
            </li>
        
          </ul>
        </section>
      </main>
    </div>
  );
};

export default PrivacyPolicy;